import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from '../hooks/use-i18n'
import type { Locale } from '../hooks/use-i18n'

const LOCALES: Locale[] = ['en', 'fr', 'es', 'ru', 'it', 'de', 'pt', 'hy']

type LanguageSelectorProps = {
  /** Expanded list for mobile nav drawer — no floating dropdown */
  variant?: 'dropdown' | 'inline'
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ variant = 'dropdown' }) => {
  const { locale, setLocale, flags, localeNames, t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (variant === 'inline') return
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, variant])

  const localeButton = (loc: Locale, className: string) => (
    <button
      key={loc}
      type="button"
      onClick={() => {
        setLocale(loc)
        setOpen(false)
      }}
      className={className}
    >
      <span className="shrink-0">{flags[loc]}</span>
      <span className="truncate">{localeNames[loc]}</span>
    </button>
  )

  if (variant === 'inline') {
    return (
      <div className="grid grid-cols-2 gap-1.5">
        {LOCALES.map((loc) =>
          localeButton(
            loc,
            `flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
              locale === loc
                ? 'bg-white/15 text-white font-medium'
                : 'text-white/75 hover:bg-white/10 hover:text-white'
            }`,
          ),
        )}
      </div>
    )
  }

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 md:p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
        aria-label={t('common.language')}
        aria-expanded={open}
        title={localeNames[locale]}
      >
        <span className="text-base md:text-lg leading-none">{flags[locale]}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 py-1 w-44 max-w-[calc(100vw-1.5rem)] rounded-lg bg-[#1a1a1a] text-white border border-white/20 shadow-lg z-[60] max-h-[min(70vh,20rem)] overflow-y-auto"
          role="menu"
        >
          {LOCALES.map((loc) =>
            localeButton(
              loc,
              `w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/10 whitespace-nowrap ${
                locale === loc ? 'bg-white/10 font-medium' : ''
              }`,
            ),
          )}
        </div>
      )}
    </div>
  )
}

export default LanguageSelector
